
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, Briefcase } from 'lucide-react';
import ImageUpload from './ImageUpload';

interface ExperienceItem {
    id: string;
    company: string;
    position: string;
    duration: string;
    description: string;
    logo: string;
    logoAlt: string;
    technologies: string[];
}

export default function ExperienceManager() {
    const [experiences, setExperiences] = useState<ExperienceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<ExperienceItem>>({
        company: '',
        position: '',
        duration: '',
        description: '',
        logo: '',
        technologies: []
    });

    // Helper for tags input
    const [tagsInput, setTagsInput] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'experience')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setExperiences(data.content);
            }
        } catch (err) {
            console.error('Error fetching experience:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: ExperienceItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'experience',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setExperiences(updatedList);
            setIsFormOpen(false);
            setNewItem({ company: '', position: '', duration: '', description: '', logo: '', technologies: [] });
            setTagsInput('');
        } catch (err) {
            console.error('Error saving experience:', err);
            alert('Failed to save experience');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.company || !newItem.position) return;

        const itemToAdd: ExperienceItem = {
            id: Math.random().toString(36).substr(2, 9),
            company: newItem.company!,
            position: newItem.position!,
            duration: newItem.duration || '',
            description: newItem.description || '',
            logo: newItem.logo || '',
            logoAlt: `${newItem.company} logo`,
            technologies: tagsInput.split(',').map(t => t.trim()).filter(t => t)
        };

        const updated = [...experiences, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this experience?')) return;
        const updated = experiences.filter(i => i.id !== id);
        handleSave(updated);
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">Experience</h2>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    {isFormOpen ? 'Cancel' : <><Plus size={18} /> Add Experience</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4 animate-in fade-in slide-in-from-top-4 duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Company</label>
                            <input
                                type="text"
                                value={newItem.company}
                                onChange={(e) => setNewItem({ ...newItem, company: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Position</label>
                            <input
                                type="text"
                                value={newItem.position}
                                onChange={(e) => setNewItem({ ...newItem, position: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Duration</label>
                            <input
                                type="text"
                                value={newItem.duration}
                                onChange={(e) => setNewItem({ ...newItem, duration: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="Jan 2022 - Present"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Technologies</label>
                            <input
                                type="text"
                                value={tagsInput}
                                onChange={(e) => setTagsInput(e.target.value)}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="React, Node.js (comma separated)"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                        <textarea
                            rows={3}
                            value={newItem.description}
                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Company Logo</label>
                        <div className="max-w-xs">
                            <ImageUpload
                                value={newItem.logo || null}
                                onChange={(url) => setNewItem({ ...newItem, logo: url })}
                                folder="logos"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={addItem}
                            disabled={saving || !newItem.company}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : 'Save Experience'}
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {experiences.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col md:flex-row gap-6 group hover:border-gray-700 transition-colors">
                        <div className="flex-shrink-0">
                            {item.logo ? (
                                <img src={item.logo} alt={item.company} className="h-16 w-16 rounded-lg object-contain bg-white p-1" />
                            ) : (
                                <div className="h-16 w-16 rounded-lg bg-gray-800 flex items-center justify-center text-gray-500">
                                    <Briefcase size={24} />
                                </div>
                            )}
                        </div>
                        <div className="flex-grow">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{item.position}</h3>
                                    <p className="text-blue-400 font-medium">{item.company}</p>
                                    <p className="text-sm text-gray-500 mt-1">{item.duration}</p>
                                </div>
                                <button
                                    onClick={() => deleteItem(item.id)}
                                    className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                            <p className="text-gray-400 mt-3 text-sm leading-relaxed">{item.description}</p>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {item.technologies.map((tech, i) => (
                                    <span key={i} className="px-2 py-1 rounded-md bg-gray-800 text-xs text-gray-300 border border-gray-700">
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}

                {experiences.length === 0 && !loading && (
                    <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No experience added yet.
                    </div>
                )}
            </div>
        </div>
    );
}
